import { ExportCellValue, ExportTable } from './exportTypes';

/**
 * Byte-order mark. Without it Excel on Windows decodes UTF-8 as the system ANSI codepage (1255 for
 * Hebrew) and every non-Latin character comes out as mojibake. This is the actual Hebrew fix.
 */
export const CSV_BOM = '﻿';

export const CSV_MIME = 'text/csv;charset=utf-8;';

/**
 * Values starting with these are interpreted as formulas by Excel and Sheets. Names, comments and
 * custom personal details are free text on a public booking form, so this is untrusted input landing
 * in a spreadsheet an admin opens.
 *
 * `+` and `-` are deliberately excluded: `+972...` phone numbers and negative discounts are both
 * legitimate and common here, and neither can carry a payload without also containing a function
 * name, which the `=` and `@` rules already catch.
 */
const FORMULA_PREFIXES = ['=', '@', '\t', '\r'];

const needsQuoting = (str: string): boolean =>
  str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r') || str.trim() !== str;

export const escapeCsvValue = (value: ExportCellValue): string => {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'V' : '';

  let str = String(value);
  if (FORMULA_PREFIXES.some((prefix) => str.startsWith(prefix))) {
    str = `'${str}`;
  }
  if (needsQuoting(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const buildCsv = (table: ExportTable): string => {
  const lines = [table.headers, ...table.rows].map((row) => row.map(escapeCsvValue).join(','));
  // \r\n per RFC 4180 — what Excel expects, and what the backend's csv.writer already emits.
  return CSV_BOM + lines.join('\r\n');
};

/**
 * RFC 4180 parser. Needed because the reports endpoints hand us a finished CSV string from Python's
 * csv.writer, and turning that into xlsx needs structure back. Fields containing commas and newlines
 * are quoted, so splitting on ',' is wrong — comment fields in the orders report definitely contain
 * commas.
 */
export const parseCsv = (text: string): string[][] => {
  const withoutBom = text.startsWith(CSV_BOM) ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < withoutBom.length; i += 1) {
    const char = withoutBom[i];

    if (inQuotes) {
      if (char === '"') {
        if (withoutBom[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      // eslint-disable-next-line no-continue
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      // Only terminates a row outside quotes; swallow the \n of a \r\n pair.
      if (char === '\r' && withoutBom[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

/**
 * Cells parsed out of a CSV are all strings, so an xlsx built from them shows every price as
 * left-aligned text with Excel's "number stored as text" warning. Convert what is unambiguously
 * numeric — but never something with a leading zero, because keeping `0501234567` a string is
 * precisely what xlsx buys us over CSV.
 */
export const coerceNumeric = (value: string): string | number => {
  if (!/^-?\d+(\.\d+)?$/.test(value)) return value;
  if (/^-?0\d/.test(value)) return value;
  if (value.replace(/\D/g, '').length > 15) return value;
  return Number(value);
};
